import { ServiceLocator } from 'src/app/tools/service-locator.tool';
import { CoreService } from 'src/app/services/core.service';
import { RequestOptions } from './../services/base.service';
import { map, Observable, Subscription, firstValueFrom, BehaviorSubject, finalize, of } from 'rxjs';
import { SelectStatementResult } from 'src/app/models/select-statement-result.model';
import { SelectStatementOptions } from '../models/select-statement-options.model';
import { LocalStorageService } from '../services/local-storage.service';

export class TableDataOperator<TData = any> {
	public onResponse: BehaviorSubject<SelectStatementResult<TData>> = new BehaviorSubject<SelectStatementResult<TData>>(null);

	public selectStatementResult: SelectStatementResult<TData>;

	private _defaultGetObservable = async (selectStatementOptions: SelectStatementOptions, requestOptions?: RequestOptions) => {

		if (this._options.localStorageKey) {
			const data = this._localStorageService.get<SelectStatementResult<TData>>(this._options.localStorageKey);

			if (data) {
				return of(data);
			}
		}

		return (await this._coreService.getSelectStatementData(
			selectStatementOptions,
			requestOptions
		)).pipe(
			map(
				(response: SelectStatementResult<TData>) => {
					this.selectStatementResult = response;

					if (this._TDataFactory) {
						// First letters of property names to lowercase. (C# object returns property names in uppercase if no type is specified and DTO is just of type `object`).
						this.selectStatementResult.data = this.selectStatementResult.data.map(
							(item: TData) => {
								let transformedItem: TData = new this._TDataFactory();

								for (let propertyKey in item) {

									// Check if it's not inherited property.
									if (item.hasOwnProperty(propertyKey)) {
										transformedItem[propertyKey.charAt(0).toLowerCase() + propertyKey.slice(1)] = item[propertyKey];
									}
								}

								return transformedItem;
							}
						);
					}

					if (this._options.localStorageKey) {
						this._localStorageService.set<SelectStatementResult<TData>>(this._options.localStorageKey, response);
					}

					return response;
				}
			),
			finalize(
				() => {
					this.onResponse.next(this.selectStatementResult);
				}
			)
		);
	};
	public get defaultGetObservable() {
		return this._defaultGetObservable;
	}

	private _defaultLoad = async (selectStatementOptions: SelectStatementOptions, requestOptions?: RequestOptions) => (await this._options.getObservable(selectStatementOptions, requestOptions)).subscribe();
	public get defaultLoad() {
		return this._defaultLoad;
	}

	public getObservable = (requestOptions?: RequestOptions, selectStatementOptions: SelectStatementOptions = this.defaultSelectStatementOptions): Promise<Observable<SelectStatementResult<TData>>> => this._options.getObservable(selectStatementOptions, requestOptions);
	public getObservablePromise = async (requestOptions?: RequestOptions, selectStatementOptions: SelectStatementOptions = this.defaultSelectStatementOptions): Promise<SelectStatementResult<TData>> => firstValueFrom(await this._options.getObservable(selectStatementOptions, requestOptions));

	public load = (requestOptions?: RequestOptions, selectStatementOptions: SelectStatementOptions = this.defaultSelectStatementOptions): Promise<Subscription> => this._options.load(selectStatementOptions, requestOptions);

	private _coreService: CoreService = ServiceLocator.injector.get(CoreService);
	private _localStorageService: LocalStorageService = ServiceLocator.injector.get(LocalStorageService);

	constructor(
		public defaultSelectStatementOptions?: SelectStatementOptions,
		private _TDataFactory?: new() => TData,
		private _options: {
			getObservable?: (selectStatementOptions: SelectStatementOptions, requestOptions?: RequestOptions) => Promise<Observable<SelectStatementResult<TData>>>,
			load?: (selectStatementOptions: SelectStatementOptions, requestOptions?: RequestOptions) => Promise<Subscription>,
			localStorageKey?: string
		} = {
			getObservable: (selectStatementOptions: SelectStatementOptions, requestOptions?: RequestOptions) => this.defaultGetObservable(selectStatementOptions, requestOptions),
			load: (selectStatementOptions: SelectStatementOptions, requestOptions?: RequestOptions) => this._defaultLoad(selectStatementOptions, requestOptions),
		}
	) {}
}

